import { createSlice } from "@reduxjs/toolkit";
import { accumulateProgressSteps } from "../../components/Workspace/lib/progressAccumulator.js";
import { closeRunningProgressCards } from "../../components/Workspace/lib/closeRunningCards.js";
import { upsertTaskSummary } from "../../components/Workspace/lib/taskSummaryUpsert.js";
import { compareBrainEvents } from "../../components/Workspace/lib/brainContract.js";

const initialState = {
  messages: [],
  brainEvents: [],
  taskStatus: null,
  wsStage: "initialize",
};

const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    messagesSet(state, action) {
      state.messages = Array.isArray(action.payload) ? action.payload : [];
    },
    // ``prepare`` stamps every appended message with a ``ts`` (epoch ms)
    // so the chat can render a local-timezone clock label next to it.
    // A caller that already knows the real event time — the WS hook
    // (backend ``timestamp``) or history replay — passes its own ``ts``
    // and that always wins; the Date.now() default only covers messages
    // created client-side (e.g. the user's own bubble). Date.now() lives
    // in ``prepare``, not the reducer, to keep the reducer pure.
    messageAppended: {
      reducer(state, action) {
        state.messages.push(action.payload);
      },
      prepare(msg) {
        return {
          payload:
            msg && typeof msg === "object" && msg.ts == null
              ? { ...msg, ts: Date.now() }
              : msg,
        };
      },
    },
    messagesAppended: {
      reducer(state, action) {
        const arr = Array.isArray(action.payload) ? action.payload : [];
        for (const m of arr) state.messages.push(m);
      },
      prepare(msgs) {
        const now = Date.now();
        const arr = Array.isArray(msgs) ? msgs : [];
        return {
          payload: arr.map((m) =>
            m && typeof m === "object" && m.ts == null ? { ...m, ts: now } : m,
          ),
        };
      },
    },
    // Wraps the pure ``accumulateProgressSteps`` helper so the live
    // WS path can stay aligned with parseTaskHistory (replay) — both
    // call the same accumulator, so live and refresh views can't
    // silently diverge.
    progressStepsAccumulated(state, action) {
      const { steps, ts } = action.payload || {};
      state.messages = accumulateProgressSteps(state.messages, steps, ts);
    },
    // Wraps ``closeRunningProgressCards``. Used by the done / error /
    // cancelled WS branches to flip any still-running cards to a
    // terminal state before the result/error/cancelled card lands.
    progressCardsClosed(state, action) {
      const { ts, terminalStatus } = action.payload || {};
      state.messages = closeRunningProgressCards(
        state.messages,
        ts,
        terminalStatus ? { terminalStatus } : undefined,
      );
    },
    // Wraps ``upsertTaskSummary`` so the task_summary WS event lands
    // idempotently — preventing the duplicate-card bug when
    // /task/restart has already rebuilt the summary from
    // conversation_log and the WebSocket subsequently replays it
    // (harmoniq-558).
    taskSummaryUpserted(state, action) {
      state.messages = upsertTaskSummary(state.messages, action.payload);
    },
    // Replaces the list — used to CLEAR it when the run on screen
    // changes. Loading the replayed history goes through
    // ``brainEventsMerged`` instead, so it can't wipe live lines.
    brainEventsSet(state, action) {
      state.brainEvents = Array.isArray(action.payload) ? action.payload : [];
    },
    // Merges a replayed batch (``GET /task/{id}/brain``) into the list by
    // id. The replay fetch and the live WS tail race: a live line that
    // lands while the fetch is in flight must survive the fetch
    // resolving, and a refetch after a reconnect must fill the gap
    // without duplicating what is already there. Existing entries win;
    // the result is ordered by ``ts``, then ``id``.
    brainEventsMerged(state, action) {
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      const seen = new Set(state.brainEvents.map((e) => e?.id));
      let added = false;
      for (const evt of incoming) {
        if (!evt || !evt.id || seen.has(evt.id)) continue;
        seen.add(evt.id);
        state.brainEvents.push(evt);
        added = true;
      }
      if (added) state.brainEvents.sort(compareBrainEvents);
    },
    // Dedup by id — matches the live WS handler's "if (prev.some(e => e.id === evt.id)) return prev"
    // pattern so replay + live tail can interleave without double-rendering.
    // The event goes in at its ``ts``-then-``id`` position, not at the
    // end: same-millisecond events can arrive out of id order, and the
    // live list must read exactly as the replayed one will.
    brainEventAppended(state, action) {
      const evt = action.payload;
      if (!evt || !evt.id) return;
      if (state.brainEvents.some((e) => e.id === evt.id)) return;
      let at = state.brainEvents.length;
      while (at > 0 && compareBrainEvents(state.brainEvents[at - 1], evt) > 0) at--;
      state.brainEvents.splice(at, 0, evt);
    },
    taskStatusSet(state, action) {
      state.taskStatus = action.payload ?? null;
    },
    wsStageSet(state, action) {
      const next = action.payload;
      if (typeof next !== "string" || !next.trim()) return;
      state.wsStage = next;
    },
    workspaceReset() {
      return initialState;
    },
  },
});

export const {
  messagesSet,
  messageAppended,
  messagesAppended,
  progressStepsAccumulated,
  progressCardsClosed,
  taskSummaryUpserted,
  brainEventsSet,
  brainEventsMerged,
  brainEventAppended,
  taskStatusSet,
  wsStageSet,
  workspaceReset,
} = workspaceSlice.actions;

export const selectMessages = (state) => state.workspace.messages;
export const selectBrainEvents = (state) => state.workspace.brainEvents;
export const selectTaskStatus = (state) => state.workspace.taskStatus;
export const selectWsStage = (state) => state.workspace.wsStage;

export default workspaceSlice.reducer;
